import {
    CHANNEL_TYPES,
    CONNECTOR_TYPES,
    PROVIDER_TYPES,
    PROVIDER_TO_CHANNEL_MAP,
} from '@/vars/general'

export type ChannelTableDisplayItem = {
    isActive?: boolean
    connectorType?: string
    providerType?: string
    connectorId?: string
    id?: string
    channelId?: string
    channelType?: string
    isDraft?: boolean
    phone?: string
    isChisai?: boolean
    createdAt?: number
    alive?: boolean
    name?: string
    title?: string
}

type ConnectorAvatar = {
    type?: string
    color: string
    symbol: string
    image: string | null
    name: string
}

const DEFAULT_AVATAR: ConnectorAvatar = {
    color: 'white',
    symbol: '?',
    image: null,
    name: 'Неизвестно',
}

const NOT_FOUND_AVATAR: ConnectorAvatar = {
    color: 'white',
    symbol: '404',
    image: null,
    name: 'Не найдено',
}

type ChannelTableDisplayOptions = {
    effectiveConnectors: ChannelTableDisplayItem[]
    connectorsAvatars: ConnectorAvatar[]
    whatsappBasisNames?: Record<string, string>
    isChannelsRework?: boolean
}

export function getEffectiveConnectors(
    connectors: ChannelTableDisplayItem[] = [],
    extraConnectors: ChannelTableDisplayItem[] = []
): ChannelTableDisplayItem[] {
    const byId = new Map<string, ChannelTableDisplayItem>()
    const withoutId: ChannelTableDisplayItem[] = []

    const addConnector = (connector?: ChannelTableDisplayItem | null) => {
        const key = connector?.connectorId || connector?.id || null
        if (!key) {
            if (connector) withoutId.push(connector)
            return
        }
        if (!byId.has(String(key))) {
            byId.set(String(key), connector)
        }
    }

    connectors.forEach(addConnector)
    extraConnectors.forEach(addConnector)

    return [...byId.values(), ...withoutId]
}

export function getChannelIndex(
    channelType: string,
    connector: ChannelTableDisplayItem | null | undefined,
    effectiveConnectors: ChannelTableDisplayItem[]
): number {
    let targetChannelType: string | null = null

    if (
        [
            CHANNEL_TYPES.whatsappChannel,
            PROVIDER_TYPES.whatsappApi,
            PROVIDER_TYPES.whatsappBasis,
            PROVIDER_TYPES.whatsapp,
            PROVIDER_TYPES.whatsappDh,
        ].includes(channelType)
    ) {
        targetChannelType = CHANNEL_TYPES.whatsappChannel
    } else if (
        [CHANNEL_TYPES.telegramChannel, PROVIDER_TYPES.telegramApi, PROVIDER_TYPES.telegramDh].includes(
            channelType
        )
    ) {
        targetChannelType = CHANNEL_TYPES.telegramChannel
    } else if ([CHANNEL_TYPES.maxChannel, PROVIDER_TYPES.maxBasis, PROVIDER_TYPES.maxDh].includes(channelType)) {
        targetChannelType = CHANNEL_TYPES.maxChannel
    } else if ([CHANNEL_TYPES.maxBusiness, PROVIDER_TYPES.maxBusiness].includes(channelType)) {
        targetChannelType = CHANNEL_TYPES.maxBusiness
    } else if ([CHANNEL_TYPES.smsChannel, PROVIDER_TYPES.smsGate].includes(channelType)) {
        targetChannelType = CHANNEL_TYPES.smsChannel
    } else {
        return 1
    }

    const channelsInGroup = effectiveConnectors
        .filter(item => !item.isChisai && item.connectorType === targetChannelType)
        .sort((a, b) => {
            const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
            const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
            if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return aTime - bTime
            if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
            if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
            return String(a.connectorId || '').localeCompare(String(b.connectorId || ''))
        })

    if (connector) {
        const currentIndex = channelsInGroup.findIndex(
            item => item.connectorId === connector.connectorId
        )
        return currentIndex >= 0 ? currentIndex + 1 : channelsInGroup.length + 1
    }

    return channelsInGroup.length + 1
}

export function getChannelDisplayName(channelType: string, index: number): string {
    const baseNames: Record<string, string> = {
        [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
        [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
        [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
        [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
        [PROVIDER_TYPES.telegramApi]: 'Telegram',
        [PROVIDER_TYPES.telegramDh]: 'Telegram',
        [PROVIDER_TYPES.maxBasis]: 'MAX',
        [PROVIDER_TYPES.maxDh]: 'MAX',
        [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
        [PROVIDER_TYPES.smsGate]: 'SMS',
    }
    const baseName = baseNames[channelType] || channelType
    return index === 1 ? baseName : `${baseName} ${index}`
}

export function getConnectorPhone(
    item: ChannelTableDisplayItem,
    effectiveConnectors: ChannelTableDisplayItem[]
): string {
    if (!item || item.isActive === false) return ''
    const connectorId = item.connectorId
    if (!connectorId) return item.phone || ''
    const liveConnector = effectiveConnectors.find(connector => connector.connectorId === connectorId)
    return liveConnector?.phone || item.phone || ''
}

export function getConnectorAvatarForTable(
    item: ChannelTableDisplayItem,
    options: ChannelTableDisplayOptions
): ConnectorAvatar {
    const { effectiveConnectors, connectorsAvatars, whatsappBasisNames = {}, isChannelsRework = false } =
        options

    if (item.isActive === false) {
        const channelType = PROVIDER_TO_CHANNEL_MAP[item.channelType as keyof typeof PROVIDER_TO_CHANNEL_MAP]
        const avatar = connectorsAvatars.find(entry => entry.type === channelType) || DEFAULT_AVATAR
        const baseNames: Record<string, string> = {
            [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
            [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
            [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
            [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
            [PROVIDER_TYPES.telegramApi]: 'Telegram',
            [PROVIDER_TYPES.telegramDh]: 'Telegram',
            [PROVIDER_TYPES.maxBasis]: 'MAX',
            [PROVIDER_TYPES.maxDh]: 'MAX',
            [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
            [PROVIDER_TYPES.smsGate]: 'SMS',
        }
        const baseName = baseNames[item.channelType || ''] || item.channelType || 'Канал'

        let targetChannelType: string | null = null
        if (
            [
                PROVIDER_TYPES.whatsappApi,
                PROVIDER_TYPES.whatsappBasis,
                PROVIDER_TYPES.whatsapp,
                PROVIDER_TYPES.whatsappDh,
            ].includes(item.channelType || '')
        ) {
            targetChannelType = CHANNEL_TYPES.whatsappChannel
        } else if ([PROVIDER_TYPES.telegramApi, PROVIDER_TYPES.telegramDh].includes(item.channelType || '')) {
            targetChannelType = CHANNEL_TYPES.telegramChannel
        } else if (item.channelType === PROVIDER_TYPES.maxBasis || item.channelType === PROVIDER_TYPES.maxDh) {
            targetChannelType = CHANNEL_TYPES.maxChannel
        } else if (item.channelType === PROVIDER_TYPES.maxBusiness) {
            targetChannelType = CHANNEL_TYPES.maxBusiness
        } else if (item.channelType === PROVIDER_TYPES.smsGate) {
            targetChannelType = CHANNEL_TYPES.smsChannel
        }

        let channelIndex = 1
        if (targetChannelType) {
            channelIndex = getChannelIndex(targetChannelType, null, effectiveConnectors)
        }

        const displayName = channelIndex === 1 ? baseName : `${baseName} ${channelIndex}`
        return { ...avatar, name: displayName }
    }

    const isWhatsappBasis = item.connectorType === CONNECTOR_TYPES.whatsappBasis
    const isChannel =
        Object.values(CHANNEL_TYPES).includes(item.connectorType || '') ||
        (item.providerType && Object.values(PROVIDER_TYPES).includes(item.providerType)) ||
        Object.values(PROVIDER_TYPES).includes(item.connectorType || '')

    const avatarType = isChannel ? item.connectorType : item.providerType || item.connectorType
    const baseAvatar = connectorsAvatars.find(entry => entry.type === avatarType) || NOT_FOUND_AVATAR

    if (isChannel) {
        const providerType = item.providerType || item.connectorType || ''
        if (isChannelsRework && isWhatsappBasis) {
            const cachedName = whatsappBasisNames[item.connectorId || '']
            if (cachedName) {
                let avatar = baseAvatar
                if (cachedName.includes('MAX')) {
                    const maxAvatar = connectorsAvatars.find(entry => entry.type === CHANNEL_TYPES.maxChannel)
                    if (maxAvatar) avatar = maxAvatar
                } else if (cachedName.includes('Telegram')) {
                    const tgAvatar = connectorsAvatars.find(
                        entry => entry.type === CHANNEL_TYPES.telegramChannel
                    )
                    if (tgAvatar) avatar = tgAvatar
                }
                return { ...avatar, name: cachedName }
            }
            return { ...baseAvatar, name: 'WhatsApp *' }
        }

        const channelIndex = getChannelIndex(providerType, item, effectiveConnectors)
        const displayName = getChannelDisplayName(providerType, channelIndex)
        return { ...baseAvatar, name: displayName }
    }

    return baseAvatar
}

const EMPTY_SLOT_DISPLAY_AVATAR: ConnectorAvatar = {
    color: 'grey lighten-2',
    symbol: '—',
    image: null,
    name: 'Свободный канал',
}

function findConnectorForChannel(
    channel: ChannelTableDisplayItem | null | undefined,
    connectors: ChannelTableDisplayItem[]
): ChannelTableDisplayItem | undefined {
    if (!channel) return undefined
    const connectorId = String(channel.connectorId || channel.id || '')
    const channelId = String(channel.channelId || channel.id || '')
    return connectors.find(item => {
        const itemConnectorId = String(item.connectorId || item.id || '')
        const itemChannelId = String(item.channelId || '')
        return Boolean(
            (connectorId && itemConnectorId === connectorId)
            || (channelId && itemChannelId && itemChannelId === channelId)
        )
    })
}

export function getProjectChannelTableDisplay(
    channel: ChannelTableDisplayItem | null | undefined,
    options: ChannelTableDisplayOptions
): {
    name: string
    phone: string
    empty: boolean
    avatar: ConnectorAvatar
} {
    if (!channel) {
        return {
            name: 'Свободный канал',
            phone: '',
            empty: true,
            avatar: EMPTY_SLOT_DISPLAY_AVATAR,
        }
    }

    const connector = findConnectorForChannel(channel, options.effectiveConnectors)
    const connectorId = String(
        connector?.connectorId || connector?.id || channel.connectorId || channel.id || ''
    )
    const tableItem: ChannelTableDisplayItem = {
        ...(connector || {}),
        connectorId,
        isActive: true,
    }
    const avatar = connector
        ? getConnectorAvatarForTable(tableItem, options)
        : EMPTY_SLOT_DISPLAY_AVATAR
    const rawPhone = connector
        ? getConnectorPhone(tableItem, options.effectiveConnectors)
        : ''

    return {
        name: connector
            ? (avatar.name || channel.name || channel.title || 'Канал')
            : (channel.name || channel.title || 'Канал'),
        phone: rawPhone || channel.phone || '',
        empty: false,
        avatar,
    }
}
